import { useEffect, useMemo, useRef, useState, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCZK, formatCZKCompact, plur } from "@/lib/format";
import { Layers, CalendarDays, TrendingUp, Receipt, ChevronDown } from "lucide-react";
import { PageHeader, StatCard, useIsMobile } from "@/components/shared/PagePrimitives";
import { FilterBar } from "@/components/shared/FilterBar";
import { FilterPanel } from "@/components/shared/FilterPanel";
import { ColumnChart, TipRow, monthName, monthShort, cap } from "@/components/economics/overview/charts";
import "@/components/economics/overview/ecoOverview.css";
import EconomicsDocuments from "@/pages/EconomicsDocuments";

// Režie (229, varianta A): KPI · Podle kategorie · Podle měsíce · doklady po měsících.
// Klik na fakturu nebo účtenku otevře detail na místě (bez přesměrování do Dokladů).
// Barva kategorie je podle pořadí kategorií firmy, ne podle velikosti — filtr ji nepřebarví.
const CAT_PALETTE = ["#e0703f", "#1f8a5b", "#3f74e0", "#9b6be8", "#d49a12", "#c2417a", "#14a3a3", "#7d8696"];
const OTHER = "#9aa1ad";
const KIND_TABS = [
  { value: "all", label: "Vše" }, { value: "faktura", label: "Faktury" },
  { value: "uctenka", label: "Účtenky" }, { value: "pokladna", label: "Pokladna" }, { value: "sklad", label: "Sklad" },
];
const SERIES = { faktura: "FP", uctenka: "UC" };
const money = (v) => (Math.abs(v) >= 1e6 ? formatCZKCompact(v) : formatCZK(v));   // KPI: plné číslo, od milionu zkráceně
const shortDate = (iso) => {
  if (!iso) return "—";
  const [, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)}. ${Number(m)}.`;
};
const docWord = (n) => plur(n, "doklad", "doklady", "dokladů");
// „Dodavatel · č. 123" → název a druhý řádek; účtenka „účtenka", sklad/pokladna zbytek štítku.
function splitLabel(r) {
  const parts = (r.label || "").split(" · ");
  if (r.kind === "uctenka") return { name: r.label || "Účtenka", sub: "účtenka" };
  if (r.kind === "sklad" || r.kind === "pokladna") return { name: parts[0] || "—", sub: parts.slice(1).join(" · ") };
  return { name: parts[0] || "—", sub: [parts.slice(1).join(" · "), r.lines].filter(Boolean).join(" · ") };
}

function StatusPill({ r }) {
  if (r.is_credit_note) return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-destructive/[0.08] text-destructive whitespace-nowrap">Dobropis</span>;
  const st = r.kind === "uctenka" ? "Zaplaceno" : r.payment_status;
  if (!st) return <span className="text-muted-foreground">—</span>;
  if (st === "Zaplaceno") return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-success/[0.09] text-success whitespace-nowrap">Zaplaceno</span>;
  if (st === "Částečně zaplaceno") return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-blue-500/[0.09] text-blue-600 dark:text-blue-400 whitespace-nowrap">Částečně</span>;
  return <span className="text-[13px] font-bold rounded-full px-3 py-1 bg-amber-500/[0.09] text-amber-600 dark:text-amber-400 whitespace-nowrap">Nezaplaceno</span>;
}

function NoChip({ r }) {
  const s = SERIES[r.kind];
  return s && r.no ? (
    <span className="inline-flex h-7 items-center rounded-lg bg-secondary px-2 text-[12.5px] font-bold tabular-nums text-foreground/75 whitespace-nowrap">{s} {r.no}</span>
  ) : <span className="text-muted-foreground text-sm">—</span>;
}

const GRID = "grid-cols-[84px_70px_minmax(0,2.2fr)_minmax(0,1.3fr)_120px_120px]";

export default function ExpensesOverview({ embedded = false }) {
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const thisYear = String(new Date().getFullYear());
  const [year, setYear] = useState(thisYear);
  const [kind, setKind] = useState("all");
  const [catF, setCatF] = useState("all");
  const [monthF, setMonthF] = useState("all");
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q.trim().toLowerCase());
  const [panelOpen, setPanelOpen] = useState(false);
  const [openMonths, setOpenMonths] = useState({});
  const openDocRef = useRef(null);

  const load = () => api.get("/economics/expenses").then((r) => setData(r.data)).catch(() => setData((d) => d || { monthly: [], months: [] }));
  useEffect(() => {
    load();
    window.addEventListener("pp-doc-saved", load);
    return () => window.removeEventListener("pp-doc-saved", load);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMonthF("all"); }, [year]);

  const years = useMemo(() => {
    const ys = new Set((data?.monthly || []).map((m) => m.month.slice(0, 4)));
    ys.add(thisYear);
    return [...ys].sort().reverse();
  }, [data, thisYear]);
  const inYear = (m) => year === "all" || (m || "").startsWith(year);

  const catColor = useMemo(() => {
    const map = {};
    (data?.category_names || []).forEach((c, i) => { map[c] = CAT_PALETTE[i] || OTHER; });
    return (c) => map[c] || OTHER;
  }, [data]);

  // Součty za rok (karty) — z měsíčních řádků podle kategorií, stejně jako dřív.
  const categories = useMemo(() => {
    const acc = {};
    (data?.monthly_by_category || []).filter((r) => inYear(r.month)).forEach((r) => {
      (data.category_names || []).forEach((c) => { acc[c] = (acc[c] || 0) + (r[c] || 0); });
    });
    return Object.entries(acc).map(([name, total]) => ({ name, total: Math.round(total) }))
      .filter((c) => c.total !== 0).sort((a, b) => b.total - a.total);
  }, [data, year]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = categories.reduce((s, c) => s + c.total, 0);

  const yearRows = useMemo(() => (data?.monthly || []).filter((m) => inYear(m.month)), [data, year]); // eslint-disable-line react-hooks/exhaustive-deps
  const monthly = useMemo(() => (year === "all" ? yearRows.slice(-12) : yearRows), [yearRows, year]);
  const withData = monthly.filter((m) => m.total !== 0);
  // 401: průměr ze stejných měsíců jako součet — u Rok = Vše dělil součet všech let jen posledními 12 měsíci
  const avgMonths = yearRows.filter((m) => m.total !== 0).length;
  const avg = avgMonths ? Math.round(total / avgMonths) : 0;
  const lastMonth = withData[withData.length - 1];
  const lastMonthRows = (data?.months || []).find((m) => m.month === lastMonth?.month);
  const basisWord = data?.basis === "gross" ? "s DPH" : "bez DPH";

  // Seznam: rok + druh + kategorie + měsíc + hledání; součty měsíců z vyfiltrovaných řádků.
  const listMonths = useMemo(() => (data?.months || [])
    .filter((m) => inYear(m.month) && (monthF === "all" || m.month === monthF))
    .map((m) => {
      const rows = m.purchase_rows.filter((r) => (kind === "all" || r.kind === kind) && (catF === "all" || r.category === catF)
        && (!dq || [r.label, r.category, SERIES[r.kind] && r.no ? `${SERIES[r.kind]} ${r.no}` : "", r.no ? `${SERIES[r.kind] || ""}${r.no}` : ""]
          .join(" ").toLowerCase().includes(dq.replace(/^#/, ""))));
      return { ...m, rows, sum: Math.round(rows.reduce((a, r) => a + (r.amount || 0), 0)) };
    })
    .filter((m) => m.rows.length > 0), [data, year, kind, catF, monthF, dq]); // eslint-disable-line react-hooks/exhaustive-deps
  const kindCounts = useMemo(() => {
    const c = {};
    (data?.months || []).filter((m) => inYear(m.month)).forEach((m) => m.purchase_rows.forEach((r) => { c[r.kind] = (c[r.kind] || 0) + 1; }));
    return c;
  }, [data, year]); // eslint-disable-line react-hooks/exhaustive-deps
  // Pokladna a Sklad jen když v nich něco je; na mobilu jen Vše / Faktury / Účtenky (ať se lišta vejde na řádek).
  const kindTabs = KIND_TABS.filter((t) => ["all", "faktura", "uctenka"].includes(t.value) || kind === t.value || (!mobile && kindCounts[t.value]));
  const filtering = kind !== "all" || catF !== "all" || monthF !== "all" || !!dq;
  const isOpen = (m, i) => (openMonths[m] ?? (i === 0 || filtering));

  const openRow = (r) => {
    if (r.kind === "faktura" || r.kind === "uctenka") return openDocRef.current?.(r.kind, r.ref_id);
    if (r.kind === "sklad") return navigate("/sklad");
    if (r.kind === "pokladna") return navigate("/ekonomika/pokladna");
    if (r.kind === "dochazka") return navigate("/dochazka");     // 386: práce bez zakázky
  };

  const filterCount = [year !== thisYear, catF !== "all", monthF !== "all"].filter(Boolean).length;
  const clearAll = () => { setYear(thisYear); setKind("all"); setCatF("all"); setMonthF("all"); setQ(""); };
  const chips = [
    year !== thisYear && { key: "y", label: year === "all" ? "Všechny roky" : `Rok ${year}`, clear: () => setYear(thisYear), testid: "chip-rz-year" },
    catF !== "all" && { key: "c", label: catF, clear: () => setCatF("all"), testid: "chip-rz-cat" },
    monthF !== "all" && { key: "m", label: cap(monthName(monthF)), clear: () => setMonthF("all"), testid: "chip-rz-month" },
  ].filter(Boolean);

  if (!data) return <div className="text-muted-foreground" data-testid="expenses-loading">Načítám…</div>;

  const yLabel = year === "all" ? "celkem" : year;
  const top = categories[0];
  return (
    <div className="space-y-5" data-testid="expenses-page">
      {!embedded && <PageHeader title="Režie" />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard icon={Layers} label={`Režie ${yLabel}`} value={money(total)} title={formatCZK(total)} sub={basisWord} testid="exp-stat-total" />
        <StatCard icon={CalendarDays} label="Průměr za měsíc" value={money(avg)} title={formatCZK(avg)}
          sub={avgMonths ? `z ${plur(avgMonths, "měsíce", "měsíců", "měsíců")}` : "—"} testid="exp-stat-avg" />
        <StatCard icon={TrendingUp} label="Největší kategorie" value={top?.name || "—"} valueClass="truncate" title={top?.name}
          sub={top ? `${formatCZK(top.total)} · ${total ? Math.round((top.total / total) * 100) : 0} %` : ""} testid="exp-stat-top" />
        <StatCard icon={Receipt} label={lastMonth ? cap(monthName(lastMonth.month)) : "Poslední měsíc"} value={money(lastMonth?.total || 0)}
          title={formatCZK(lastMonth?.total || 0)} sub={lastMonthRows ? docWord(lastMonthRows.count) : "—"} testid="exp-stat-month" />
      </div>

      {total === 0 ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground" data-testid="expenses-empty">
          Za zvolené období nejsou žádné režijní výdaje. Nahrajte účtenky nebo faktury bez přiřazené zakázky.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-[20px] p-4 md:p-5" data-testid="exp-categories">
            <h3 className="font-heading font-bold text-[17px] mb-2">Podle kategorie</h3>
            <div className="space-y-0.5">
              {categories.map((c, i) => {
                const pct = total ? Math.round((c.total / total) * 100) || (c.total > 0 ? 0.5 : 0) : 0;
                const on = catF === c.name;
                return (
                  <button type="button" key={c.name} data-testid={`exp-cat-${i}`} onClick={() => setCatF(on ? "all" : c.name)} aria-pressed={on}
                    className={`block w-full text-left rounded-xl px-2 py-2 transition-colors hover:bg-secondary/60 ${on ? "bg-brand/10" : ""} ${catF !== "all" && !on ? "opacity-50" : ""}`}>
                    <span className="flex items-center justify-between gap-3 text-[15px] mb-1.5">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: catColor(c.name) }} />
                        <span className="truncate">{c.name}</span>
                      </span>
                      <span className="shrink-0 tabular-nums whitespace-nowrap"><b className="font-semibold">{formatCZK(c.total)}</b> <span className="text-muted-foreground">· {pct < 1 ? "<1" : pct} %</span></span>
                    </span>
                    <span className="block h-2 rounded-full bg-secondary overflow-hidden">
                      <span className="block h-full rounded-full" style={{ width: `${Math.max(pct, 1)}%`, background: catColor(c.name) }} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-card border border-border rounded-[20px] p-4 md:p-5 eco-ov" data-testid="exp-monthly-chart">
            <h3 className="font-heading font-bold text-[17px] mb-3">Podle měsíce</h3>
            <ColumnChart compact={mobile} height={mobile ? 200 : 260} thinLabels={mobile && monthly.length > 9}
              columns={monthly.map((m) => ({
                label: monthShort(m.month), title: monthName(m.month), aria: `${cap(monthName(m.month))}: ${formatCZK(m.total)}`,
                onClick: () => setMonthF(monthF === m.month ? "all" : m.month),
                bars: [{ value: m.total, color: "var(--ov-out)", faded: monthF !== "all" && monthF !== m.month }],
                tip: (<><div className="tt">{cap(monthName(m.month))}</div><TipRow color="var(--ov-out)" value={formatCZK(m.total)} label="režie" /></>),
              }))} />
          </div>
        </div>
      )}

      <div className="relative">
        <FilterBar testid="rz-filterbar" mobileIcons
          segments={[{ tabs: kindTabs.map((t) => ({ ...t, testid: `rz-kind-${t.value}` })), value: kind, onChange: setKind, testid: "rz-kind", compact: mobile }]}
          filterCount={filterCount} onFilterClick={() => setPanelOpen((o) => !o)}
          search={q} onSearch={setQ} searchPlaceholder="Dodavatel, číslo, FP 152…"
          chips={chips} onClearAll={clearAll} />
        {panelOpen && (
          <FilterPanel open={panelOpen} onClose={() => setPanelOpen(false)} mobile={mobile} width={560}
            filterCount={filterCount} resultCount={listMonths.reduce((a, m) => a + m.rows.length, 0)}
            plural={(n) => (n === 1 ? "doklad" : n >= 2 && n <= 4 ? "doklady" : "dokladů")}
            values={{ year, cat: catF, month: monthF }}
            setValue={(k, v) => (k === "year" ? setYear(v) : k === "cat" ? setCatF(v) : setMonthF(v))}
            clear={() => { setYear(thisYear); setCatF("all"); setMonthF("all"); }}
            groups={[
              { key: "year", label: "Rok", layout: "pills", options: [...years.map((y) => ({ value: y, label: y })), { value: "all", label: "Vše" }] },
              { key: "cat", label: "Kategorie", layout: "list", options: [{ value: "all", label: "Vše" }, ...categories.map((c) => ({ value: c.name, label: c.name }))] },
              { key: "month", label: "Měsíc", layout: "list", options: [{ value: "all", label: "Vše" }, ...monthly.filter((m) => m.total !== 0).slice().reverse().map((m) => ({ value: m.month, label: cap(monthName(m.month)) }))] },
            ]} />
        )}
      </div>

      <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="exp-months">
        {!mobile && (
          <div className={`grid ${GRID} gap-3 px-5 py-3.5 border-b border-border text-[12px] font-bold uppercase tracking-[0.05em] text-muted-foreground`}>
            <span>Číslo</span><span>Datum</span><span>Doklad</span><span>Kategorie</span><span>Stav</span><span className="text-right">Částka</span>
          </div>
        )}
        {listMonths.length === 0 && <p className="px-5 py-6 text-muted-foreground">{filtering ? "Pro zvolený filtr tu nic není." : "V režii zatím nic není."}</p>}
        {listMonths.map((m, i) => {
          const open = isOpen(m.month, i);
          return (
            <div key={m.month || "none"} data-testid={`exp-month-${m.month || "none"}`}>
              <button type="button" onClick={() => setOpenMonths((o) => ({ ...o, [m.month]: !open }))} aria-expanded={open}
                className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 bg-secondary/50 border-b border-border text-left hover:bg-secondary">
                <span className="flex items-center gap-2 min-w-0">
                  <ChevronDown size={16} className={`shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
                  <span className="font-bold truncate">{m.month ? cap(monthName(m.month)) : "Bez data"}</span>
                  <span className="text-muted-foreground text-sm whitespace-nowrap">· {docWord(m.rows.length)}</span>
                </span>
                <span className="font-bold tabular-nums whitespace-nowrap" title={formatCZK(m.sum)}>{formatCZK(m.sum)}</span>
              </button>
              {open && m.rows.map((r) => <Row key={`${r.kind}-${r.ref_id}-${r.category}`} r={r} mobile={mobile} catColor={catColor} onOpen={openRow} />)}
            </div>
          );
        })}
      </div>

      {/* Detail faktury / účtenky na místě (229) */}
      <EconomicsDocuments dialogsOnly openRef={openDocRef} onChanged={load} />
    </div>
  );
}

function Row({ r, mobile, catColor, onOpen }) {
  const { name, sub } = splitLabel(r);
  const neg = r.amount < 0;
  const amount = <span className={`font-heading font-extrabold tabular-nums whitespace-nowrap ${neg ? "text-destructive" : ""}`}>{neg ? "−" : ""}{formatCZK(Math.abs(r.amount))}</span>;
  const testid = `exp-row-${r.kind}-${r.ref_id}`;
  if (mobile) {
    return (
      <button type="button" onClick={() => onOpen(r)} data-testid={testid}
        className="w-full text-left px-4 py-3 border-b border-border last:border-b-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 items-center hover:bg-muted/40">
        <span className="font-semibold truncate">{name}</span>
        {amount}
        <span className="text-[13px] text-muted-foreground truncate">
          {SERIES[r.kind] && r.no ? <b className="font-semibold text-foreground/70">{SERIES[r.kind]} {r.no} · </b> : null}
          {shortDate(r.date)} · <span className="inline-block w-2 h-2 rounded-full align-middle mr-1" style={{ background: catColor(r.category) }} />{r.category}
        </span>
        <span className="justify-self-end scale-90 origin-right"><StatusPill r={r} /></span>
      </button>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(r)} data-testid={testid}
      className={`w-full text-left grid ${GRID} gap-3 px-5 py-3.5 border-b border-border last:border-b-0 items-center hover:bg-muted/40 transition-colors`}>
      <span><NoChip r={r} /></span>
      <span className="tabular-nums text-[15px]">{shortDate(r.date)}</span>
      <span className="min-w-0">
        <span className="block font-semibold truncate">{name}</span>
        {sub && <span className="block text-[13px] text-muted-foreground truncate">{sub}</span>}
      </span>
      <span className="text-[15px] truncate" title={r.category}>{r.category}</span>
      <span><StatusPill r={r} /></span>
      <span className="text-right">{amount}</span>
    </button>
  );
}
